import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  Shield,
  Send,
  FileCheck,
  Copy,
  Check,
  AlertTriangle,
  Trash2,
  Clock,
  ExternalLink,
} from "lucide-react";
import { recruitingAPI } from "../services/recruitingApi";
import GdprHistoryTimeline from "./GdprHistoryTimeline";
import {
  GdprStatus,
  GdprStatusResponse,
  GdprHistoryItem,
  GdprHistoryResponse,
  ConsentRequestResponse,
} from "../types/gdpr.types";
import "./CandidateGdprSection.css";

interface CandidateGdprSectionProps {
  candidateId: number;
  candidateFirstName: string;
  userRole: string;
}

const STATUS_CONFIG: Record<
  string,
  { labelKey: string; color: string; icon: React.ReactNode }
> = {
  none: {
    labelKey: "candidates.gdprStatus.noConsent",
    color: "status-none",
    icon: <AlertTriangle size={16} />,
  },
  pending: {
    labelKey: "candidates.gdprStatus.pending",
    color: "status-pending",
    icon: <Clock size={16} />,
  },
  given: {
    labelKey: "candidates.gdprStatus.consentGiven",
    color: "status-given",
    icon: <Check size={16} />,
  },
  withdrawn: {
    labelKey: "candidates.gdprStatus.withdrawn",
    color: "status-withdrawn",
    icon: <AlertTriangle size={16} />,
  },
  deleted: {
    labelKey: "candidates.gdprStatus.dataDeleted",
    color: "status-deleted",
    icon: <Trash2 size={16} />,
  },
};

const CandidateGdprSection: React.FC<CandidateGdprSectionProps> = ({
  candidateId,
  candidateFirstName,
  userRole,
}) => {
  const { t } = useTranslation(["recruiting", "common"]);
  const queryClient = useQueryClient();
  const [showConsentLink, setShowConsentLink] = useState(false);
  const [consentLink, setConsentLink] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  const [showExternalModal, setShowExternalModal] = useState(false);
  const [externalFormUrl, setExternalFormUrl] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteNotes, setDeleteNotes] = useState("");
  const [activeView, setActiveView] = useState<"status" | "history">("status");

  // Fetch GDPR status
  const { data: gdprStatusData, isLoading: statusLoading } =
    useQuery<GdprStatusResponse>(
      ["candidate-gdpr", candidateId],
      () =>
        recruitingAPI.getCandidateGdpr(
          candidateId,
        ) as Promise<GdprStatusResponse>,
      { enabled: !!candidateId },
    );

  // Fetch GDPR history
  const { data: gdprHistoryData, isLoading: historyLoading } =
    useQuery<GdprHistoryResponse>(
      ["candidate-gdpr-history", candidateId],
      () =>
        recruitingAPI.getCandidateGdprHistory(
          candidateId,
        ) as Promise<GdprHistoryResponse>,
      { enabled: !!candidateId },
    );

  // Request consent mutation
  const [mutationError, setMutationError] = useState<string | null>(null);
  const requestConsentMutation = useMutation(
    () => recruitingAPI.requestGdprConsent(candidateId, "internal"),
    {
      onSuccess: (data) => {
        const response = data as ConsentRequestResponse;
        if (response.success && response.data) {
          setConsentLink(response.data.consentFormUrl);
          setShowConsentLink(true);
          setMutationError(null);
          queryClient.invalidateQueries(["candidate-gdpr", candidateId]);
          queryClient.invalidateQueries([
            "candidate-gdpr-history",
            candidateId,
          ]);
        } else {
          console.error("Unexpected response format:", data);
          setMutationError("Unexpected response from server");
        }
      },
      onError: (
        error: Error & {
          response?: { data?: { message?: string; error?: string } };
        },
      ) => {
        console.error("Error requesting consent:", error);
        const errorDetails =
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          "Failed to request consent";
        setMutationError(errorDetails);
      },
    },
  );

  // Record external consent mutation
  const recordExternalMutation = useMutation(
    (formUrl: string) =>
      recruitingAPI.recordExternalGdprConsent(candidateId, formUrl),
    {
      onSuccess: () => {
        setShowExternalModal(false);
        setExternalFormUrl("");
        setMutationError(null);
        queryClient.invalidateQueries(["candidate-gdpr", candidateId]);
        queryClient.invalidateQueries(["candidate-gdpr-history", candidateId]);
      },
      onError: (
        error: Error & {
          response?: { data?: { message?: string; error?: string } };
        },
      ) => {
        console.error("Error recording external consent:", error);
        const errorDetails =
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          "Failed to record external consent";
        setMutationError(errorDetails);
      },
    },
  );

  // Delete data mutation (admin only)
  const deleteDataMutation = useMutation(
    (notes: string) => recruitingAPI.deleteGdprData(candidateId, notes),
    {
      onSuccess: () => {
        setShowDeleteModal(false);
        setDeleteNotes("");
        setMutationError(null);
        queryClient.invalidateQueries(["candidate-gdpr", candidateId]);
        queryClient.invalidateQueries(["candidate-gdpr-history", candidateId]);
        queryClient.invalidateQueries(["candidate", candidateId.toString()]);
      },
      onError: (
        error: Error & {
          response?: { data?: { message?: string; error?: string } };
        },
      ) => {
        console.error("Error deleting GDPR data:", error);
        const errorDetails =
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          "Failed to delete data";
        setMutationError(errorDetails);
      },
    },
  );

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(consentLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const gdprStatus = gdprStatusData?.data;
  const gdprHistory = gdprHistoryData?.data || [];
  // Treat null/undefined status as 'none' (for existing candidates without GDPR status set)
  const effectiveStatus = gdprStatus?.status || "none";
  const statusConfig = STATUS_CONFIG[effectiveStatus] || STATUS_CONFIG.none;

  const isAdmin =
    userRole === "application_admin" || userRole === "organization_admin";
  const canRequestConsent =
    effectiveStatus === "none" || effectiveStatus === "withdrawn";
  const canRecordExternal = canRequestConsent;
  const canDeleteData =
    isAdmin && effectiveStatus !== "deleted" && effectiveStatus !== "none";

  if (statusLoading) {
    return (
      <div className="gdpr-section-loading">
        <div className="spinner"></div>
        <p>{t("gdpr.loadingStatus")}</p>
      </div>
    );
  }

  return (
    <div className="candidate-gdpr-section">
      {/* View Toggle */}
      <div className="gdpr-view-toggle">
        <button
          className={`toggle-btn ${activeView === "status" ? "active" : ""}`}
          onClick={() => setActiveView("status")}
        >
          <Shield size={16} /> {t("gdpr.statusAndActions")}
        </button>
        <button
          className={`toggle-btn ${activeView === "history" ? "active" : ""}`}
          onClick={() => setActiveView("history")}
        >
          <Clock size={16} /> {t("gdpr.history", { count: gdprHistory.length })}
        </button>
      </div>

      {activeView === "status" && (
        <div className="gdpr-status-view">
          {/* Status Card */}
          <div className="gdpr-status-card">
            <div className="status-header">
              <h3>{t("gdpr.consentStatus")}</h3>
            </div>
            <div className={`status-badge-large ${statusConfig.color}`}>
              {statusConfig.icon}
              <span>{t(statusConfig.labelKey)}</span>
            </div>

            {gdprStatus?.isGrandfathered && (
              <div className="grandfathered-notice">
                <Shield size={14} />
                <span>{t("gdpr.grandfatheredNotice")}</span>
              </div>
            )}

            {gdprStatus?.activeConsent && (
              <div className="consent-details">
                <div className="detail-row">
                  <span className="label">{t("gdpr.consentType")}</span>
                  <span className="value">
                    {gdprStatus.activeConsent.consent_type}
                  </span>
                </div>
                {gdprStatus.activeConsent.consent_given &&
                  gdprStatus.activeConsent.consent_given_at && (
                    <div className="detail-row">
                      <span className="label">{t("gdpr.givenAt")}</span>
                      <span className="value">
                        {new Date(
                          gdprStatus.activeConsent.consent_given_at,
                        ).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                  )}
                {gdprStatus.activeConsent.consent_withdrawn &&
                  gdprStatus.activeConsent.consent_withdrawn_at && (
                    <div className="detail-row withdrawn-row">
                      <span className="label">{t("gdpr.withdrawnAt")}</span>
                      <span className="value">
                        {new Date(
                          gdprStatus.activeConsent.consent_withdrawn_at,
                        ).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                  )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="gdpr-actions">
            <h4>{t("gdpr.actions")}</h4>

            {mutationError && (
              <div
                className="error-message"
                style={{
                  color: "#dc2626",
                  backgroundColor: "#fef2f2",
                  padding: "8px 12px",
                  borderRadius: "6px",
                  marginBottom: "12px",
                  fontSize: "14px",
                }}
              >
                <AlertTriangle
                  size={14}
                  style={{ marginRight: "6px", verticalAlign: "middle" }}
                />
                {mutationError}
              </div>
            )}

            <div className="action-buttons">
              {canRequestConsent && (
                <button
                  className="btn btn-primary gdpr-action-btn"
                  onClick={() => requestConsentMutation.mutate()}
                  disabled={requestConsentMutation.isLoading}
                >
                  <Send size={16} />
                  {requestConsentMutation.isLoading
                    ? t("gdpr.generating")
                    : t("gdpr.requestConsent")}
                </button>
              )}

              {canRecordExternal && (
                <button
                  className="btn btn-secondary gdpr-action-btn"
                  onClick={() => setShowExternalModal(true)}
                >
                  <FileCheck size={16} />
                  {t("gdpr.recordExternalConsent")}
                </button>
              )}

              {canDeleteData && (
                <button
                  className="btn btn-danger gdpr-action-btn"
                  onClick={() => setShowDeleteModal(true)}
                >
                  <Trash2 size={16} />
                  {t("gdpr.deleteDataGdpr")}
                </button>
              )}
            </div>

            {gdprStatus?.status === "pending" && gdprStatus.activeConsent && (
              <div className="pending-consent-info">
                <p>
                  {t("gdpr.pendingConsentMessage", {
                    name: candidateFirstName,
                  })}
                </p>
                <div className="consent-link-box">
                  <input
                    type="text"
                    readOnly
                    value={`${window.location.origin}/consent/${gdprStatus.activeConsent.consent_token}`}
                    className="consent-link-input"
                  />
                  <button
                    className="btn btn-outline copy-btn"
                    onClick={() => {
                      navigator.clipboard.writeText(
                        `${window.location.origin}/consent/${gdprStatus.activeConsent!.consent_token}`,
                      );
                      setLinkCopied(true);
                      setTimeout(() => setLinkCopied(false), 2000);
                    }}
                  >
                    {linkCopied ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                  <a
                    href={`${window.location.origin}/consent/${gdprStatus.activeConsent.consent_token}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline"
                  >
                    <ExternalLink size={16} />
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Consent Link Modal */}
          {showConsentLink && (
            <div
              className="modal-overlay"
              onClick={() => setShowConsentLink(false)}
            >
              <div
                className="modal gdpr-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <h2>{t("gdpr.consentLinkGenerated")}</h2>
                  <button
                    className="close-btn"
                    onClick={() => setShowConsentLink(false)}
                  >
                    ×
                  </button>
                </div>
                <div className="modal-body">
                  <p>
                    {t("gdpr.shareLinkMessage", { name: candidateFirstName })}
                  </p>
                  <div className="consent-link-box">
                    <input
                      type="text"
                      readOnly
                      value={consentLink}
                      className="consent-link-input"
                    />
                    <button
                      className="btn btn-primary"
                      onClick={handleCopyLink}
                    >
                      {linkCopied ? (
                        <>
                          <Check size={16} /> {t("gdpr.copied")}
                        </>
                      ) : (
                        <>
                          <Copy size={16} /> {t("gdpr.copy")}
                        </>
                      )}
                    </button>
                  </div>
                  <div className="link-actions">
                    <a
                      href={consentLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline"
                    >
                      <ExternalLink size={16} /> {t("gdpr.previewForm")}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* External Consent Modal */}
          {showExternalModal && (
            <div
              className="modal-overlay"
              onClick={() => setShowExternalModal(false)}
            >
              <div
                className="modal gdpr-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <h2>{t("gdpr.recordExternalConsentTitle")}</h2>
                  <button
                    className="close-btn"
                    onClick={() => setShowExternalModal(false)}
                  >
                    ×
                  </button>
                </div>
                <div className="modal-body">
                  <p>
                    {t("gdpr.recordExternalDescription", {
                      name: candidateFirstName,
                    })}
                  </p>
                  <div className="form-group">
                    <label htmlFor="external-form-url">
                      {t("gdpr.externalConsentFormUrl")}{" "}
                      <span className="required">*</span>
                    </label>
                    <input
                      type="url"
                      id="external-form-url"
                      value={externalFormUrl}
                      onChange={(e) => setExternalFormUrl(e.target.value)}
                      placeholder={t("gdpr.externalFormPlaceholder")}
                      className="form-input"
                      required
                    />
                    <span className="form-hint">
                      {t("gdpr.externalFormHint")}
                    </span>
                  </div>
                  <div className="modal-actions">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setShowExternalModal(false)}
                    >
                      {t("common:actions.cancel")}
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() =>
                        recordExternalMutation.mutate(externalFormUrl)
                      }
                      disabled={
                        recordExternalMutation.isLoading ||
                        !externalFormUrl.trim()
                      }
                    >
                      {recordExternalMutation.isLoading
                        ? t("gdpr.recording")
                        : t("gdpr.recordConsent")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Delete Data Modal */}
          {showDeleteModal && (
            <div
              className="modal-overlay"
              onClick={() => setShowDeleteModal(false)}
            >
              <div
                className="modal gdpr-modal delete-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header warning">
                  <AlertTriangle size={24} />
                  <h2>{t("gdpr.deleteCandidateData")}</h2>
                  <button
                    className="close-btn"
                    onClick={() => setShowDeleteModal(false)}
                  >
                    ×
                  </button>
                </div>
                <div className="modal-body">
                  <div className="warning-box">
                    <p>
                      <strong>{t("gdpr.warning")}</strong>{" "}
                      {t("gdpr.deleteWarningMessage", {
                        name: candidateFirstName,
                      })}
                    </p>
                    <p>{t("gdpr.thisIncludes")}</p>
                    <ul>
                      <li>{t("gdpr.deleteIncludes.contactInfo")}</li>
                      <li>{t("gdpr.deleteIncludes.resumeCv")}</li>
                      <li>{t("gdpr.deleteIncludes.linkedIn")}</li>
                      <li>{t("gdpr.deleteIncludes.applicationNotes")}</li>
                    </ul>
                    <p>{t("gdpr.cannotBeUndone")}</p>
                  </div>
                  <div className="form-group">
                    <label htmlFor="delete-notes">
                      {t("gdpr.reasonForDeletion")}
                    </label>
                    <textarea
                      id="delete-notes"
                      value={deleteNotes}
                      onChange={(e) => setDeleteNotes(e.target.value)}
                      placeholder={t("gdpr.deletionReasonPlaceholder")}
                      rows={3}
                      required
                    />
                  </div>
                  <div className="modal-actions">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setShowDeleteModal(false)}
                    >
                      {t("common:actions.cancel")}
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => deleteDataMutation.mutate(deleteNotes)}
                      disabled={
                        deleteDataMutation.isLoading || !deleteNotes.trim()
                      }
                    >
                      {deleteDataMutation.isLoading
                        ? t("common:loading.deleting")
                        : t("gdpr.deleteDataPermanently")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeView === "history" && (
        <div className="gdpr-history-view">
          <GdprHistoryTimeline
            history={gdprHistory}
            isLoading={historyLoading}
          />
        </div>
      )}
    </div>
  );
};

export default CandidateGdprSection;
